// src/components/ReportExportPane.jsx
// レポート出力（出席記録ページの一部）【2026-09-04 / 09-07 レイアウト変更】
//
// 元は /reports の独立ページ。授業を選んでから使う画面なので、
// 出席記録の中に集計・出席CSV取り込みと並べて置いている。
// 授業の選択は親ページの授業名セレクトが担うので、ここでは形式と期間だけ指定する。
//
// レイアウト: 中央にプレビュー、右に設定。
//   設定を変えるとプレビューが自動で更新されるので、
//   「出してみたら思っていたのと違った」が起きない。
//   プレビューとファイルは同じデータから作っている。

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';

// 文字サイズの段階【2026-09-11】
// 見本（excel-converter.html の adjustTableFontSize）と同じ考え方。
// 「何段階かをボタンで選べる」方が、こちらが最適値を決め打ちするより早い。
// 表だけに効かせる。まわりの文字まで動くと、かえって読みにくくなる。
const FONT_STEPS  = [0.78, 0.85, 0.92, 1.0, 1.1, 1.25];
const FONT_LABELS = ['極小', '小', '標準', '中', '大', '特大'];
const FONT_KEY    = 'reportPreviewFontStep';

// 期間のプリセット【2026-09-11】
//   日付を2つ手で入れるのも、カレンダーを2回開くのも面倒なので、
//   授業で実際に使う範囲をボタンにする。
//   学期は日本の大学の一般的な区切り（前期 4/1〜9/30、後期 10/1〜翌3/31）。
//   ここに無い範囲は「自分で指定」で入れる。
const ymdLocal = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
};

function termRange(base, which) {
  const y = base.getFullYear();
  const m = base.getMonth() + 1;          // 1〜12
  const inSpring = m >= 4 && m <= 9;      // 今が前期か
  let spring = inSpring;                  // which='current' のとき
  let year = y;
  if (which === 'previous') {
    spring = !inSpring;
    // 前期(4〜9月)にいるなら、1つ前の学期は前年度の後期
    if (inSpring) year = y - 1;
    // 後期(10〜3月)にいるなら、1つ前は同じ年度の前期。
    // ただし1〜3月は年度としては前年なので、前期は前年の4月始まり
    else if (m <= 3) year = y - 1;
  } else if (!inSpring && m <= 3) {
    // 1〜3月は「前年度の後期」の途中
    year = y - 1;
  }
  return spring
    ? { start: year + '-04-01', end: year + '-09-30' }
    : { start: year + '-10-01', end: (year + 1) + '-03-31' };
}

const PERIOD_PRESETS = [
  { key: 'all',   label: '全期間', range: () => ({ start: '', end: '' }) },
  { key: 'term',  label: '今学期', range: () => termRange(new Date(), 'current') },
  { key: 'prev',  label: '前の学期', range: () => termRange(new Date(), 'previous') },
  { key: 'month', label: '今月', range: () => {
      const n = new Date();
      return { start: ymdLocal(new Date(n.getFullYear(), n.getMonth(), 1)),
               end:   ymdLocal(new Date(n.getFullYear(), n.getMonth() + 1, 0)) };
    } },
  { key: 'lastmonth', label: '先月', range: () => {
      const n = new Date();
      return { start: ymdLocal(new Date(n.getFullYear(), n.getMonth() - 1, 1)),
               end:   ymdLocal(new Date(n.getFullYear(), n.getMonth(), 0)) };
    } },
  { key: 'd30',   label: '直近30日', range: () => {
      const n = new Date();
      return { start: ymdLocal(new Date(n.getFullYear(), n.getMonth(), n.getDate() - 29)),
               end:   ymdLocal(n) };
    } },
];

// 書き出せる形【2026-09-11】
// singleOnly: 1つの授業を見ているときだけ出す（全授業モードでは意味がない）
const EXPORT_KINDS = [
  {
    key: 'xlsx', label: 'Excel (.xlsx)',
    desc: ({ sheetCount }) =>
      '付箋の全' + sheetCount + '枚がシートとして入ります。罫線・色つきで、普段使いはこちら',
  },
  {
    key: 'csv', label: 'CSV',
    desc: ({ isAll }) =>
      (isAll ? '授業別サマリの1枚だけ' : '出席簿の1枚だけ')
      + '。他システムへの取り込みや一括加工向け（UTF-8 BOM付き）',
  },
  {
    key: 'pdf', label: 'PDF', singleOnly: true,
    desc: '印刷・配布向けの固定レイアウト。左で選んだ「出力の形」や付箋は反映されません',
  },
  {
    key: 'log', label: '出席ログ (CSV)',
    desc: '氏名なし。授業名・日付・学籍番号・状態だけ。名簿をサーバーに置かない運用向け',
  },
];

function loadFontStep() {
  try {
    const v = parseInt(localStorage.getItem(FONT_KEY), 10);
    if (v >= 0 && v < FONT_STEPS.length) return v;
  } catch (e) { /* 参照できなくても既定値で動く */ }
  return 2; // 標準
}

// 出席簿に足せる集計。どれも既存の出席データから計算できるもの。
// 小テスト・提出物・感想などは元データを持っていないので対象外。
const EXTRA_SHEETS = [
  { key: 'trend',        label: '回ごとの推移',   desc: '各授業日の出席・遅刻・早退・欠席の人数と授業メモ' },
  { key: 'risk',         label: '要注意の学生',   desc: '連続欠席・累計欠席・出席率が低い学生と該当理由' },
  { key: 'detail',       label: '打刻明細',       desc: '1行1記録。時刻・打刻方法・信頼度つき' },
  { key: 'unregistered', label: 'アカウント未作成', desc: '名簿にはいるが、まだアプリに登録していない学生' },
  { key: 'audit',        label: '変更履歴',       desc: '出席記録を手で直した証跡' },
];

// 横スクロールしても左に残す列と、その幅（px）。
// 見本の出席簿と同じで、まず学籍番号と氏名が見えていること。
const PIN_WIDTH = { '学籍番号': 104, '氏名': 130, '名前': 130, '授業名': 150 };

export default function ReportExportPane({ classId, classes = [], periodQuery = '', setErr, setMsg }) {
  // classId が 'all' のときは全授業サマリを出す
  const isAll = String(classId) === 'all';

  const [start, setStart]   = useState('');
  const [end, setEnd]       = useState('');
  const [busy, setBusy]     = useState(false);
  const [format, setFormat] = useState('matrix');

  const [preview, setPreview]         = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  // 出力ダイアログ【2026-09-11】
  //   形式ごとにボタンを並べると、押す前に「何が出るのか」が分からない。
  //   ボタンは1つにして、押してから形式と中身を見せて選ばせる。
  const [askOpen, setAskOpen] = useState(false);
  const [askKind, setAskKind] = useState('xlsx');

  // 表の文字サイズ（見本と同じ6段階）
  const [fontStep, setFontStep] = useState(loadFontStep);
  const changeFont = (d) => {
    const v = Math.max(0, Math.min(FONT_STEPS.length - 1, fontStep + d));
    setFontStep(v);
    try { localStorage.setItem(FONT_KEY, String(v)); } catch (e) { /* 保存できなくても動く */ }
  };

  // 表の上にも横スクロールバーを置く【2026-09-11】
  //   出席簿は横に長いので、下端のスクロールバーまで行かないと横に動かせない。
  //   見本（cross-top-scroll）と同じで、上にもバーを出して本体と同期させる。
  //   syncLock が無いと、片方を動かす→もう片方のscrollイベント→……で振動する。
  const bodyRef = useRef(null);
  const topRef  = useRef(null);
  const syncLock = useRef(false);
  const [scrollW, setScrollW] = useState(0);   // 0 なら横にはみ出していない

  const onTopScroll = () => {
    if (syncLock.current) { syncLock.current = false; return; }
    syncLock.current = true;
    if (bodyRef.current && topRef.current) bodyRef.current.scrollLeft = topRef.current.scrollLeft;
  };
  const onBodyScroll = () => {
    if (syncLock.current) { syncLock.current = false; return; }
    syncLock.current = true;
    if (bodyRef.current && topRef.current) topRef.current.scrollLeft = bodyRef.current.scrollLeft;
  };

  useEffect(() => {
    if (!askOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setAskOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [askOpen]);

  const measure = useCallback(() => {
    const b = bodyRef.current;
    if (!b) { setScrollW(0); return; }
    setScrollW(b.scrollWidth > b.clientWidth + 1 ? b.scrollWidth : 0);
  }, []);

  // 出席簿に足す集計シート【2026-09-07】
  // 既定は「回ごとの推移」「要注意の学生」。
  // 名簿外の学生は出席簿そのものに並ぶようになったので、別シートは廃止した【2026-09-07】。
  const [sheets, setSheets] = useState(['trend', 'risk']);
  // 中央に何を表示するか【2026-09-07】
  //   'register' = 出席簿（全授業なら授業別サマリ）
  //   'classes'  = 授業ごとの出席簿（上の授業バーで1つ選ぶ）
  //   'overview' = 授業のまとめ / それ以外 = 追加シートのキー
  const [showing, setShowing] = useState('register');

  // 上の授業バー。全授業のときだけ出す（実施が1回でもある授業）
  const classTabs = useMemo(
    () => (classes || []).filter(c => Number(c.held_sessions) > 0),
    [classes]
  );
  const [activeClass, setActiveClass] = useState('');

  useEffect(() => {
    if (classTabs.length === 0) { if (activeClass) setActiveClass(''); return; }
    if (!classTabs.some(c => String(c.id) === String(activeClass))) {
      setActiveClass(String(classTabs[0].id));
    }
  }, [classTabs, activeClass]);

  // 左の付箋。シートの種類を縦に並べる。授業ごとの出席簿は1枚にまとめて置き、
  // どの授業かは上のバーで選ばせる（授業が多いと縦に伸びすぎるため）。
  const sideTabs = useMemo(() => {
    const list = [];
    if (isAll) {
      list.push({ key: 'register', label: '授業別サマリ' });
      if (classTabs.length > 0) list.push({ key: 'classes', label: '授業ごとの出席簿' });
    } else if (format === 'summary') {
      return [{ key: 'register', label: '出席集計' }];
    } else {
      list.push({ key: 'overview', label: '授業のまとめ' });
      list.push({ key: 'register', label: '出席簿' });
    }
    EXTRA_SHEETS.filter(sh => sheets.includes(sh.key))
      .forEach(sh => list.push({ key: sh.key, label: sh.label }));
    return list;
  }, [isAll, classTabs, sheets, format]);

  // ファイルに入るシートの枚数（授業ごとの出席簿は授業の数だけ増える）
  const sheetCount = sideTabs.length
    + (sideTabs.some(t => t.key === 'classes') ? classTabs.length - 1 : 0);

  // 上の授業バーを出すのは「授業ごとの出席簿」を選んでいるときだけ
  const perClass = isAll && showing === 'classes' && classTabs.length > 0;

  const toggleSheet = (key) =>
    setSheets(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (start) params.append('startDate', start);
    if (end)   params.append('endDate', end);
    if (!isAll && format === 'summary') params.append('format', 'summary');
    if (sheets.length > 0) params.append('sheets', sheets.join(','));
    // すべての授業は、画面で見ている期間の授業だけ【2026-09-17】
    if (isAll && periodQuery) new URLSearchParams(periodQuery).forEach((v, k) => params.append(k, v));
    return params;
  }, [start, end, format, isAll, sheets, periodQuery]);

  const loadPreview = useCallback(async () => {
    setPreviewBusy(true);
    try {
      // 「授業ごとの出席簿」は、上のバーで選んだ授業の出席簿をそのまま見せる
      const targetId = perClass ? activeClass : classId;
      if (perClass && !targetId) { setPreview(null); return; }

      const params = new URLSearchParams();
      params.append('limit', 'all');   // 全行を出す（表はスクロールできる）【2026-09-29】
      if (start) params.append('startDate', start);
      if (end)   params.append('endDate', end);
      if (perClass)     params.append('format', 'matrix');
      else if (!isAll)  params.append('format', format);
      if (!perClass && showing !== 'register') params.append('sheet', showing);
      // 「授業のまとめ」は出席簿と同じ集計範囲で作るので format は送らない
      if (isAll && !perClass && periodQuery) new URLSearchParams(periodQuery).forEach((v, k) => params.append(k, v));

      const url = ((isAll && !perClass)
          ? '/api/reports/all/preview'
          : '/api/reports/class/' + targetId + '/preview')
        + (params.toString() ? '?' + params.toString() : '');
      const res = await fetch(url, {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') }
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'プレビューの取得に失敗しました');
      setPreview(d);
    } catch (e) { setErr(e.message); setPreview(null); }
    finally     { setPreviewBusy(false); }
  }, [classId, isAll, format, start, end, showing, perClass, activeClass, periodQuery]); // eslint-disable-line

  // 表示中の付箋が無くなったら（チェックを外した、形式を変えた）先頭に戻す
  useEffect(() => {
    if (!sideTabs.some(t => t.key === showing)) setShowing('register');
  }, [sideTabs, showing]);

  // 表が横にはみ出しているかを測る。中身・シート・文字サイズが変わるたび。
  // 描画の直後だと幅が確定していないことがあるので、1フレーム待ってから測る。
  useEffect(() => {
    const id = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(id); window.removeEventListener('resize', measure); };
  }, [preview, showing, fontStep, perClass, activeClass, measure]);

  // 設定を変えたらプレビューを取り直す
  useEffect(() => { loadPreview(); }, [loadPreview]);

  /**
   * 書き出すファイル名【2026-09-07 の命名を関数に切り出した / 2026-09-11】
   * 元の出席簿ジェネレータに合わせた名前。出力ダイアログでも先に見せる。
   */
  const outName = (kind) => {
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const cname = (preview && preview.className) || String(classId);
    const safe  = cname.replace(/[\\/:*?"<>|]/g, '_');
    if (kind === 'log') return '出席ログ_' + (isAll ? '全授業' : safe) + '_' + stamp + '.csv';
    if (isAll)          return '出席簿_集約_' + stamp + '.' + kind;
    if (kind === 'pdf') return 'レポート_' + safe + '_' + stamp + '.pdf';
    return (format === 'summary' ? '出席集計_' : '出席簿_') + safe + '_' + stamp + '.' + kind;
  };

  const download = async (kind) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const params = buildParams();
      const url = (isAll ? '/api/reports/all/' + kind
                         : '/api/reports/class/' + classId + '/' + kind)
        + (params.toString() ? '?' + params.toString() : '');
      const res = await fetch(url, {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') }
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error('HTTP ' + res.status + ' ' + text.slice(0, 200));
      }
      const blob   = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a      = document.createElement('a');
      a.href       = objUrl;
      a.download   = outName(kind);
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
      setMsg('ダウンロードを開始しました');
    } catch (e) { setErr(e.message); }
    finally     { setBusy(false); }
  };

  // 左端に固定する列（見出しの名前で決める。連続している分だけ）
  const pins = useMemo(() => {
    const map = {};
    let left = 0;
    const cols = (preview && preview.columns) || [];
    for (let i = 0; i < cols.length; i++) {
      const w = PIN_WIDTH[cols[i]];
      if (!w) break;                       // 途切れたらそこまで
      if (left + w > 300) break;           // 固定しすぎると本文が見えなくなる
      map[i] = { left, width: w };
      left += w;
    }
    // 全部が固定列になると横スクロールの意味が無いので、その場合は固定しない
    if (Object.keys(map).length >= cols.length) return {};
    return map;
  }, [preview]);

  const pinStyle = (i, isHeader) => {
    const p = pins[i];
    if (!p) return {};
    const last = !pins[i + 1];
    return {
      position: 'sticky',
      left:     p.left,
      width:    p.width,
      minWidth: p.width,
      maxWidth: p.width,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      zIndex:   isHeader ? 3 : 2,
      boxShadow: last ? '2px 0 0 #d1d5db' : undefined,
    };
  };

  // 出欠のセル（出席 / 遅刻 / 早退 / 欠席）に色を付ける
  const STATUS_COLOR = { '出席': '#16a34a', '遅刻': '#d97706', '早退': '#0e7490', '欠席': '#dc2626' };
  const markStyle = (v) => {
    const color = STATUS_COLOR[v];
    if (!color) return {};
    return { textAlign: 'center', color, fontWeight: v === '出席' ? 400 : 700 };
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>{isAll ? '授業の一覧を書き出す' : 'この授業の出席記録を書き出す'}</h2>
      </div>

      <div className="export-layout">
        {/* ---- 右側の設定（スマホでは上に来る） ---- */}
        <div className="export-settings">
          {/* 期間【2026-09-11 プリセット方式に変更】
              日付を2つ手で入れるのも、カレンダーを2回開くのも面倒なので、
              よく使う範囲をボタンにした。ボタンは下の入力欄に日付を入れるだけなので、
              押したあとに片方だけ直す、という使い方もできる。 */}
          <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.3rem' }}>期間</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
            {PERIOD_PRESETS.map(ps => {
              const r = ps.range();
              const active = start === r.start && end === r.end;
              return (
                <button key={ps.key} type="button"
                        className={active ? 'btn-primary' : 'btn-secondary'}
                        onClick={() => { setStart(r.start); setEnd(r.end); }}
                        title={r.start ? r.start + ' 〜 ' + r.end : '期間を絞らない'}
                        style={{ padding: '0.25rem 0.6rem', fontSize: '0.82rem' }}>
                  {ps.label}
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.35rem' }}>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} />
            <input type="date" value={end}   onChange={e => setEnd(e.target.value)} />
          </div>

          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.9rem' }}>
            {start || end
              ? <>いま <strong>{start || '最初'} 〜 {end || '最新'}</strong> で絞っています</>
              : '全期間（絞っていません）'}
          </div>


          {isAll ? (
            <p style={{ fontSize: '0.82rem', color: '#4b5563', lineHeight: 1.8, marginTop: 0 }}>
              1枚目に授業を1行ずつ並べた一覧、次に授業ごとの出席簿、
              最後に下で選んだ集計が入ります。集計は授業名の列を足して1枚にまとめます。
            </p>
          ) : (
            <>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem' }}>出力の形</div>
              <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <input type="radio" name="fmt" checked={format === 'matrix'}
                       onChange={() => setFormat('matrix')} style={{ marginTop: '0.25rem' }} />
                <span style={{ fontSize: '0.85rem' }}>
                  <strong>出欠表</strong>
                    <div style={{ fontSize: '0.8rem', color: '#4b5563', lineHeight: 1.6 }}>
                    1行1学生 × 授業日の列。総出席日数つき
                  </div>
                </span>
              </label>
              <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', marginBottom: '0.9rem' }}>
                <input type="radio" name="fmt" checked={format === 'summary'}
                       onChange={() => setFormat('summary')} style={{ marginTop: '0.25rem' }} />
                <span style={{ fontSize: '0.85rem' }}>
                  <strong>集計のみ</strong>
                  <div style={{ fontSize: '0.8rem', color: '#4b5563', lineHeight: 1.6 }}>
                    回ごとの列なし。回数と出席率だけ
                  </div>
                </span>
              </label>
            </>
          )}

          <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.3rem' }}>
            一緒に出す集計
          </div>
          <div style={{ marginBottom: '0.9rem' }}>
            {EXTRA_SHEETS.map(sh => (
              <label key={sh.key}
                     style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', marginBottom: '0.35rem' }}>
                <input type="checkbox" checked={sheets.includes(sh.key)}
                       onChange={() => toggleSheet(sh.key)} style={{ marginTop: '0.2rem' }} />
                <span style={{ fontSize: '0.82rem' }}>
                  {sh.label}
                  <div style={{ fontSize: '0.8rem', color: '#4b5563', lineHeight: 1.5 }}>{sh.desc}</div>
                </span>
              </label>
            ))}
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.3rem' }}>
              {isAll
                ? 'Excelでは別シートになります。CSVは授業別サマリだけです'
                : 'Excelでは別シートになります。CSVと「集計のみ」形式は出席簿だけです'}
            </div>
          </div>

          <button className="btn-primary" style={{ width: '100%', padding: '0.6rem' }}
                  onClick={() => {
                    // 全授業モードに切り替えたあとも PDF が選ばれたまま残らないように
                    if (isAll && askKind === 'pdf') setAskKind('xlsx');
                    setAskOpen(true);
                  }} disabled={busy}>
            書き出す…
          </button>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.3rem' }}>
            形式は押したあとで選べます
          </div>
          {busy && <p style={{ marginTop: '0.6rem', color: '#4b5563', fontSize: '0.82rem' }}>処理中...</p>}

          {/* サーバーに名簿を置きたくない先生向け【2026-09-07】
              打刻の事実だけを出し、氏名は手元の名簿で当てはめてもらう。 */}
          <div style={{ marginTop: '1rem', paddingTop: '0.8rem', borderTop: '1px dashed #d1d5db' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.3rem' }}>
              名簿を置かない運用
            </div>
            <p style={{ fontSize: '0.8rem', color: '#4b5563', lineHeight: 1.7, margin: '0 0 0.5rem' }}>
              打刻の事実（授業名・日付・学籍番号・状態）だけを書き出します。氏名は入りません。
              変換ツールに読ませ、手元の名簿と突き合わせると出席簿になります。
              名簿はその画面の中だけで使われ、どこにも送信されません。
            </p>
            <div style={{ fontSize: '0.8rem', color: '#4b5563', margin: '0 0 0.45rem' }}>
              「書き出す…」→ <strong>出席ログ</strong> を選んでください。
            </div>
            <a href="/attendance-converter.html" target="_blank" rel="noopener noreferrer"
               style={{ display: 'inline-block', marginTop: '0.45rem', fontSize: '0.8rem' }}>
              変換ツールを開く ↗
            </a>
          </div>

          <p style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.7, marginTop: '0.9rem' }}>
            中央のプレビューがそのままファイルになります。
            回として数えるのは休講でない実施済みの授業だけです。
          </p>
        </div>

        {/* ---- 中央のプレビュー ---- */}
        <div className="export-preview">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem',
                        flexWrap: 'wrap', marginBottom: '0.4rem' }}>
            <strong style={{ fontSize: '0.92rem' }}>プレビュー</strong>
            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
              付箋がそのままファイルのシートになります（全{sheetCount}枚）
            </span>
            {/* 文字サイズ（見本と同じ －／ラベル／＋ の3点セット） */}
            <div className="font-step" style={{ marginLeft: 'auto' }}>
              <button type="button" onClick={() => changeFont(-1)}
                      disabled={fontStep === 0} title="文字を小さく">－</button>
              <span title="表の文字サイズ">{FONT_LABELS[fontStep]}</span>
              <button type="button" onClick={() => changeFont(1)}
                      disabled={fontStep === FONT_STEPS.length - 1} title="文字を大きく">＋</button>
            </div>
            <button className="btn-secondary"
                    onClick={loadPreview} disabled={previewBusy}>
              {previewBusy ? '更新中…' : '↻ 更新'}
            </button>
          </div>

          {/* 表の種類が上の付箋、授業の切り替えが左の付箋【2026-09-11 入れ替え】
              もとは逆だったが、Excel のシート見出しが横並びなのに合わせた。
              表の種類は常にあるので上のバーは常に出る。授業の付箋は
              「授業ごとの出席簿」を見ているときだけ出る。 */}
          <div className="sheet-topbar-slot">
            <div className="sheet-tabs" role="tablist">
              {sideTabs.map(t => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={showing === t.key}
                  className={'sheet-tab' + (showing === t.key ? ' active' : '')}
                  onClick={() => setShowing(t.key)}
                  title={t.label}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className={'sheet-frame under-bar' + (perClass ? '' : ' no-side')}>
            {perClass && (
              <div className="sheet-tabs-v" role="tablist">
                {classTabs.map(c => (
                  <button
                    key={c.id}
                    role="tab"
                    aria-selected={String(c.id) === String(activeClass)}
                    className={'sheet-tab-v' +
                      (String(c.id) === String(activeClass) ? ' active' : '')}
                    onClick={() => setActiveClass(String(c.id))}
                    title={c.name}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}

            <div className="sheet-main">
              {/* 上にもスクロールバー。横長の表で、下端まで行かずに横へ動かせる */}
              <div className="sheet-top-scroll"
                   ref={topRef}
                   onScroll={onTopScroll}
                   style={{ display: scrollW ? 'block' : 'none' }}>
                <div className="sheet-top-scroll-inner" style={{ width: scrollW || 1 }} />
              </div>
              <div className="sheet-tabs-body" ref={bodyRef} onScroll={onBodyScroll}>
                {previewBusy && !preview ? (
                  <p style={{ color: '#6b7280', padding: '1.2rem' }}>読み込み中...</p>
                ) : !preview ? (
                  <p style={{ color: '#6b7280', padding: '1.2rem' }}>プレビューを取得できませんでした。</p>
                ) : preview.rows.length === 0 ? (
                  <p style={{ color: '#6b7280', padding: '1.5rem', textAlign: 'center' }}>
                    このシートに出せるデータがありません。
                    {!isAll && '受講者が登録されていて、授業が1回でも実施されているか確認してください。'}
                  </p>
                ) : (
                  <table className="data-table"
                         style={{ margin: 0, fontSize: FONT_STEPS[fontStep] + 'rem' }}>
                    <thead>
                      <tr>{preview.columns.map((c, i) => (
                        <th key={i}
                            className={pins[i] ? 'pin-cell' : undefined}
                            style={{ whiteSpace: 'nowrap', position: 'sticky', top: 0,
                                     background: '#f9fafb', zIndex: 1, ...pinStyle(i, true) }}>{c}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r, ri) => (
                        <tr key={ri}>
                          {r.map((v, ci) => (
                            <td key={ci}
                                className={pins[ci] ? 'pin-cell' : undefined}
                                title={pins[ci] ? String(v == null ? '' : v) : undefined}
                                style={{ whiteSpace: 'nowrap', ...markStyle(v), ...pinStyle(ci, false) }}>
                              {v === '' || v == null ? '-' : String(v)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {preview && preview.rows.length > 0 && (
            <div style={{ fontSize: '0.8rem', color: '#4b5563', marginTop: '0.35rem' }}>
              全{preview.total}行
              {preview.truncated && '（多すぎるため先頭' + preview.rows.length + '行を表示。ファイルには全行入ります）'}
            </div>
          )}
        </div>
      </div>

      {/* ---- 出力ダイアログ【2026-09-11】 ----
           形式ごとにボタンを並べていたのをやめ、1つの「書き出す…」から
           形式を選ばせる。押す前に何が出るか分からないのが不便だった。
           いまの設定（形・期間・シート枚数）と、実際に落ちるファイル名も
           ここで見せる。 */}
      {askOpen && (
        <div onClick={() => setAskOpen(false)}
             style={{ position: 'fixed', inset: 0, zIndex: 4200,
                      background: 'rgba(15,23,42,0.45)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 14, width: 'min(560px, 100%)',
                        maxHeight: '88vh', overflow: 'auto', padding: '1.3rem 1.5rem',
                        boxShadow: '0 16px 44px rgba(0,0,0,0.28)' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e3a8a' }}>
              どの形で書き出しますか？
            </h3>

            {/* いまの設定。ダイアログの中で設定を作り直させない。
                直したいときは閉じて左の設定を変えてもらう。 */}
            <div style={{ fontSize: '0.82rem', color: '#4b5563', marginTop: '0.4rem',
                          background: '#f8fafc', border: '1px solid #e2e8f0',
                          borderRadius: 8, padding: '0.5rem 0.75rem' }}>
              {isAll ? '全授業' : (preview && preview.className) || 'この授業'}
              {!isAll && '　形：' + (format === 'summary' ? '集計のみ' : '出欠表')}
              {'　期間：' + (start || end ? (start || '最初') + '〜' + (end || '最新') : '全期間')}
              {'　付箋：全' + sheetCount + '枚'}
            </div>

            <div style={{ marginTop: '0.9rem' }}>
              {EXPORT_KINDS.filter(k => !k.singleOnly || !isAll).map(k => (
                <label key={k.key}
                       style={{ display: 'flex', gap: '0.55rem', alignItems: 'flex-start',
                                padding: '0.6rem 0.7rem', marginBottom: '0.4rem',
                                border: '1px solid ' + (askKind === k.key ? '#2563eb' : '#e5e7eb'),
                                background: askKind === k.key ? '#eff6ff' : '#fff',
                                borderRadius: 10, cursor: 'pointer' }}>
                  <input type="radio" name="exportKind" checked={askKind === k.key}
                         onChange={() => setAskKind(k.key)} style={{ marginTop: '0.25rem' }} />
                  <span style={{ flex: 1 }}>
                    <strong style={{ fontSize: '0.92rem' }}>{k.label}</strong>
                    <div style={{ fontSize: '0.82rem', color: '#4b5563', lineHeight: 1.6 }}>
                      {typeof k.desc === 'function' ? k.desc({ isAll, sheetCount, format }) : k.desc}
                    </div>
                  </span>
                </label>
              ))}
            </div>

            <div style={{ fontSize: '0.82rem', color: '#4b5563', marginTop: '0.2rem' }}>
              ファイル名：<code style={{ fontSize: '0.82rem' }}>{outName(askKind)}</code>
            </div>

            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.1rem', alignItems: 'center' }}>
              <button className="btn-primary" disabled={busy}
                      onClick={async () => { await download(askKind); setAskOpen(false); }}>
                {busy ? '処理中…' : '書き出す'}
              </button>
              <button className="btn-secondary" disabled={busy} onClick={() => setAskOpen(false)}>
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
